import { getDaysInMonth } from 'date-fns/getDaysInMonth';
import { isBefore } from 'date-fns/isBefore';
import { isThisMonth } from 'date-fns/isThisMonth';
import { setDate } from 'date-fns/setDate';
import { startOfToday } from 'date-fns/startOfToday';
import { toDate } from 'date-fns/toDate';

import getFeatureDecision from '../../../../config';
import { ENABLE_DEPARTURE_TIMES } from '../../../../config/constants';
import {
  getDepartureTimesSelected,
  getMonthData,
  getMonthDataUrl,
  getPreferDirects,
  getPriceTrace,
  getSelectedInboundDate,
  getSelectedMonth,
  getSelectedOutboundDate,
  isReturn,
} from '../../selectors';
import * as serviceConstants from '../constants/dataServiceConstants';
import * as dataConstants from '../constants/dataTypeConstants';
import monthViewServiceQueryBuilder from '../interface-handling/monthViewServiceQueryBuilder';

import gridEntryReader from './gridEntryReader';

import type {
  Direction,
  MonthDataGridElement,
  MonthGridData,
  State,
} from '../../selectors/types';

export type Overrides = {
  outboundDate?: Date;
  inboundDate?: Date;
  isReturn?: boolean;
};

const getDirectAvailableKeyForDirection = (direction: Direction) => {
  if (direction === dataConstants.OUTBOUND_DIRECTION) {
    return serviceConstants.DIRECT_OUTBOUND_AVAILABLE;
  }

  if (direction === dataConstants.INBOUND_DIRECTION) {
    return serviceConstants.DIRECT_INBOUND_AVAILABLE;
  }

  throw new Error(`Invalid direction provided: ${direction}`);
};

// Helper for pairs of dates to render prices for

const getDatePair = (
  state: State,
  date: Date,
  direction: Direction,
  overrides: Overrides = {},
) => {
  if (direction === dataConstants.OUTBOUND_DIRECTION) {
    const datePair = {
      outbound: date,
      inbound: overrides.inboundDate
        ? overrides.inboundDate
        : getSelectedInboundDate(state),
    };
    return datePair;
  }

  if (direction === dataConstants.INBOUND_DIRECTION) {
    const datePair = {
      outbound: overrides.outboundDate
        ? overrides.outboundDate
        : getSelectedOutboundDate(state),
      inbound: date,
    };
    return datePair;
  }

  throw new Error(`Invalid direction provided: ${direction}`);
};
type DatePair = ReturnType<typeof getDatePair>;

// Helper to convert pair of dates into indices into grid

const getGridIndices = (datePair: DatePair, returnTrip: boolean) => {
  const gridIndices = {
    outbound: datePair.outbound.getDate() - 1,
    inbound: 0,
  };
  if (returnTrip && datePair.inbound !== null) {
    gridIndices.inbound = datePair.inbound.getDate() - 1;
  }
  return gridIndices;
};

// Priced day count helpers

const hasPrice = (
  entry: MonthDataGridElement,
  index: number,
  minIndex: number,
  direction: Direction,
) => {
  if (index < minIndex) return false;
  return (
    gridEntryReader.getSpecificPrice(entry, direction, true) ||
    gridEntryReader.getSpecificPrice(entry, direction, false)
  );
};

const getValidPricedDepartDays = (
  gridData: MonthGridData,
  minIndex: number,
) => {
  const outboundPricedEntries = gridData[gridData.length - 1].filter(
    (entry, index) =>
      hasPrice(entry, index, minIndex, dataConstants.OUTBOUND_DIRECTION),
  );
  return outboundPricedEntries.length;
};

const getValidPricedReturnDays = (
  gridData: MonthGridData,
  minIndex: number,
) => {
  if (gridData.length === 1) return 0;

  let inboundPricedCount = 0;
  for (let inbound = 0; inbound < gridData.length; inbound += 1) {
    const outbounds = gridData[inbound];
    const pricesForInbound = outbounds.filter((entry, index) =>
      hasPrice(entry, index, minIndex, dataConstants.INBOUND_DIRECTION),
    );
    if (pricesForInbound.length) inboundPricedCount += 1;
  }

  return inboundPricedCount;
};

const getLatestGrid = (state: State) => {
  const expectedUrl =
    monthViewServiceQueryBuilder.buildMonthsQueryString(state);
  const url = getMonthDataUrl(state);
  const isCheapestMonthUrl =
    `${url}`.indexOf(`/${serviceConstants.CHEAPEST_DATE}/`) !== -1;

  if (url !== expectedUrl && !isCheapestMonthUrl) {
    return null;
  }
  return getMonthData(state);
};

// Exported functions

const hasDirectsForDate = (
  state: State,
  date: Date,
  direction: Direction,
  overrides: Overrides = {},
) => {
  const grid = getLatestGrid(state);
  if (grid === null || !Array.isArray(grid) || !Array.isArray(grid[0]))
    return false;

  const availableKey = getDirectAvailableKeyForDirection(direction);
  const dates = getDatePair(state, date, direction, overrides);
  const returnTrip =
    typeof overrides.isReturn === 'boolean'
      ? overrides.isReturn
      : isReturn(state);
  const indices = getGridIndices(dates, returnTrip);

  // Prevent data race conditions where the calendar loads the grid before data is ready
  let priceEntry = null;
  if (indices.inbound < grid.length && indices.outbound < grid[0].length) {
    priceEntry = grid[indices.inbound][indices.outbound];
  }

  return priceEntry ? priceEntry[availableKey] : false;
};

const hasDirectFlight = (grid: MonthGridData | null) => {
  if (!grid) {
    return true;
  }

  const flat = grid.reduce((result, item) => result.concat(item), []);

  const hasDirect = flat.reduce(
    (result, item) => result || gridEntryReader.isEntryDirect(item, false),
    false,
  );

  return hasDirect;
};

export const getGridPricedDaysCount = (state: State, direction: Direction) => {
  const grid = getMonthData(state);
  const calendarMonth = getSelectedMonth(state, direction);
  const isCurrentMonth = calendarMonth && isThisMonth(calendarMonth);
  let minIndex = 0;
  if (isCurrentMonth) minIndex = startOfToday().getDate() - 1;

  if (direction === dataConstants.OUTBOUND_DIRECTION) {
    return getValidPricedDepartDays(grid, minIndex);
  }
  if (direction === dataConstants.INBOUND_DIRECTION) {
    return getValidPricedReturnDays(grid, minIndex);
  }

  return 0;
};

const getGridPriceData = (
  state: State,
  date: Date,
  direction: Direction,
  overrides: Overrides = {},
) => {
  const grid = getLatestGrid(state);
  if (grid === null || !Array.isArray(grid) || !Array.isArray(grid[0]))
    return { price: null, isDirect: false };

  const preferDirects = getPreferDirects(state);
  const returnTrip =
    typeof overrides.isReturn === 'boolean'
      ? overrides.isReturn
      : isReturn(state);
  const dates = getDatePair(state, date, direction, overrides);
  const indices = getGridIndices(dates, returnTrip);

  // Prevent data race conditions where the calendar loads the grid before data is ready
  let priceEntry = null;
  if (indices.inbound < grid.length && indices.outbound < grid[0].length) {
    priceEntry = grid[indices.inbound][indices.outbound];
  }

  if (!priceEntry) {
    return { price: null, isDirect: false };
  }
  const enableDepartureTimes =
    getFeatureDecision(ENABLE_DEPARTURE_TIMES, 'A') !== 'A';
  let directPrice;
  let indirectPrice;

  if (enableDepartureTimes) {
    const departureTimes = getDepartureTimesSelected(state);
    directPrice = gridEntryReader.getSpecificPriceWithTimes(
      priceEntry,
      direction,
      true,
      departureTimes,
    );
    indirectPrice = gridEntryReader.getSpecificPriceWithTimes(
      priceEntry,
      direction,
      false,
      departureTimes,
    );
  } else {
    directPrice = gridEntryReader.getSpecificPrice(priceEntry, direction, true);
    indirectPrice = gridEntryReader.getSpecificPrice(
      priceEntry,
      direction,
      false,
    );
  }

  if (preferDirects) {
    return { price: directPrice || null, isDirect: !!directPrice };
  }
  if (directPrice && indirectPrice) {
    return directPrice < indirectPrice
      ? { price: directPrice, isDirect: true }
      : { price: indirectPrice, isDirect: false };
  }
  if (indirectPrice) {
    return { price: indirectPrice, isDirect: false };
  }
  return { price: directPrice || null, isDirect: !!directPrice };
};

export type GridPriceData = ReturnType<typeof getGridPriceData>;

export const getSelectableDaysCount = (state: State, direction: Direction) => {
  let pricedDayCount = 0;
  let validDayCount = 0;

  const calendarMonth = getSelectedMonth(state, direction);

  if (!calendarMonth) {
    return { validDayCount, pricedDayCount };
  }

  const preferDirects = getPreferDirects(state);
  const daysInMonth = getDaysInMonth(calendarMonth);
  const outboundDay = getSelectedOutboundDate(state).getDate();

  for (let inboundDay = 1; inboundDay <= daysInMonth; inboundDay += 1) {
    const dayOfMonth = setDate(toDate(calendarMonth), inboundDay);
    const dateInPast = isBefore(dayOfMonth, startOfToday());
    const inboundDayBeforeOutboundDay =
      direction === dataConstants.INBOUND_DIRECTION && inboundDay < outboundDay;
    if (!dateInPast && !inboundDayBeforeOutboundDay) {
      const priceData = getGridPriceData(state, dayOfMonth, direction);
      const directFiltered =
        preferDirects && !hasDirectsForDate(state, dayOfMonth, direction);
      if (!directFiltered) {
        validDayCount += 1;
        if (priceData.price) {
          pricedDayCount += 1;
        }
      }
    }
  }

  return { validDayCount, pricedDayCount };
};

const emptySummaryData = { price: null, trace: null };

const getSelectedSummaryData = (state: State) => {
  const datePair = {
    outbound: getSelectedOutboundDate(state),
    inbound: getSelectedInboundDate(state),
  };
  const returnTrip = isReturn(state);
  const gridIndices = getGridIndices(datePair, returnTrip);

  const grid = getLatestGrid(state);
  if (!grid) return emptySummaryData;

  if (
    gridIndices.inbound >= grid.length ||
    !grid[gridIndices.inbound] ||
    gridIndices.outbound >= grid[gridIndices.inbound].length
  )
    return emptySummaryData;

  const gridEntry = grid[gridIndices.inbound][gridIndices.outbound];
  if (!gridEntry) return emptySummaryData;

  const preferDirects = getPreferDirects(state);
  const enableDepartureTimes =
    getFeatureDecision(ENABLE_DEPARTURE_TIMES, 'A') !== 'A';
  let summaryEntry;
  if (enableDepartureTimes) {
    const departureTimes = getDepartureTimesSelected(state);
    summaryEntry = gridEntryReader.getSummaryDataWithTimes(
      gridEntry,
      preferDirects,
      departureTimes,
    );
  } else {
    summaryEntry = gridEntryReader.getSummaryData(gridEntry, preferDirects);
  }
  if (!summaryEntry) return emptySummaryData;

  const summary = {
    price: summaryEntry[serviceConstants.QUOTE_PRICE],
    trace: '',
  };

  const refs = summaryEntry[serviceConstants.TRACE_REFS] ?? [];

  const traceList: Array<string | null> = [];

  refs.forEach((ref) => {
    traceList.push(getPriceTrace(state, ref));
  });
  summary.trace = traceList.join(serviceConstants.TRACE_SEPARATOR);

  return summary;
};

export default {
  getGridPriceData,
  getGridPricedDaysCount,
  getSelectableDaysCount,
  getSelectedSummaryData,
  hasDirectsForDate,
  hasDirectFlight,
};
